import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { BudgetPanel } from './BudgetPanel';
import { calculateGiftPackagePricing } from '@/lib/packagePricing';
import type { GiftPackage } from '@/lib/types';

interface Props {
  pkg: GiftPackage;
  onChanged: () => void;
}

export function CockpitTopBar({ pkg, onChanged }: Props) {
  const [expanded, setExpanded] = useState(false);

  const pricing = calculateGiftPackagePricing(pkg);
  const marginLow = pkg.margin_low_pct ?? pkg.margin_pct ?? 30;
  const marginHigh = pkg.margin_high_pct ?? pkg.margin_pct ?? 30;
  const sellingPerPkgIncl =
    pkg.quantity > 0 ? pricing.total.selling_incl_btw / pkg.quantity : 0;
  const sellingPerPkgExcl =
    pkg.quantity > 0 ? pricing.total.selling_excl_btw / pkg.quantity : 0;
  const costPerPkg = pricing.total.cost / Math.max(1, pkg.quantity);

  // Klant-budget vergelijking — gebruik de basis (excl/incl) die de klant in de wizard heeft opgegeven
  const customerBudgetBasis = pkg.budget_per_package;
  const ourPriceOnSameBasis =
    pkg.budget_type === 'incl_btw' ? sellingPerPkgIncl : sellingPerPkgExcl;
  const diffPerPkg = ourPriceOnSameBasis - customerBudgetBasis;
  const budgetBasisLabel = pkg.budget_type === 'incl_btw' ? 'incl' : 'excl';
  const tone =
    customerBudgetBasis === 0
      ? 'text-bmade-500'
      : diffPerPkg > 0.5
        ? 'text-red-600 dark:text-red-400'
        : diffPerPkg < -0.5
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-accent-600 dark:text-accent-400';
  const status =
    customerBudgetBasis === 0
      ? 'geen klant-budget'
      : diffPerPkg > 0.5
        ? `+ EUR ${diffPerPkg.toFixed(2)} over budget`
        : diffPerPkg < -0.5
          ? `EUR ${Math.abs(diffPerPkg).toFixed(2)} ruimte`
          : 'precies in budget';

  const isV2 = pricing.pricing_model_version === 'component_margin_v2';
  const marginLabel = isV2
    ? `${pricing.total.weighted_margin_pct.toFixed(1)}% gewogen`
    : marginLow === marginHigh
      ? `${marginLow.toFixed(0)}%`
      : `${marginLow.toFixed(0)}% / ${marginHigh.toFixed(0)}%`;

  return (
    <div
      className={`${
        expanded ? 'relative' : 'sticky top-0'
      } z-20 rounded-xl border border-bmade-200 bg-white/95 shadow-sm backdrop-blur dark:border-bmade-800 dark:bg-bmade-900/95`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left"
      >
        <Metric label="Kost / pkt excl" value={`EUR ${costPerPkg.toFixed(2)}`} />
        <Divider />
        <Metric label={isV2 ? 'Brutomarge 4 delen' : 'Brutomarge 9% / 21%'} value={marginLabel} slider />
        <Divider />
        <Metric label="Verkoop / pkt excl" value={`EUR ${sellingPerPkgExcl.toFixed(2)}`} />
        <Divider />
        <Metric label="Verkoop / pkt incl" value={`EUR ${sellingPerPkgIncl.toFixed(2)}`} accent />
        <Divider />
        <Metric label={`Klant (${budgetBasisLabel})`} value={status} tone={tone} />
        <Divider />
        <div className="flex flex-col text-right">
          <div className="text-[10px] uppercase tracking-wider text-bmade-500">Totaal incl</div>
          <div className="text-sm font-semibold text-bmade-950 dark:text-white">
            EUR {pricing.total.selling_incl_btw.toFixed(0)}
          </div>
        </div>
        <span className="ml-auto inline-flex shrink-0 items-center text-bmade-400">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {expanded && (
        <div className="scroll-mt-20 border-t border-bmade-200 px-4 py-3 pb-10 dark:border-bmade-800">
          <BudgetPanel pkg={pkg} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
  accent,
  slider,
}: {
  label: string;
  value: string;
  tone?: string;
  accent?: boolean;
  slider?: boolean;
}) {
  return (
    <div className="flex min-w-[8.5rem] flex-1 flex-col xl:min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-bmade-500">{label}</div>
      <div
        className={`truncate text-sm font-semibold ${
          tone ??
          (accent ? 'text-accent-600 dark:text-accent-400' : 'text-bmade-950 dark:text-white')
        }`}
      >
        {value}
        {slider && <span className="ml-1 text-[10px] font-normal text-bmade-500">▼ uitvouwen</span>}
      </div>
    </div>
  );
}

function Divider() {
  return <span className="hidden h-8 w-px bg-bmade-200 dark:bg-bmade-800 xl:block" />;
}
